import { useEffect, useState } from 'react';
import { documentsAPI, CategoryWithDocuments, FolderDetails } from '../../lib/api';
import type { DocumentHistoryEntry } from '../../lib/api/documents';
import { getApiErrorMessage } from '../../lib/api/types/common';
import { useFormatters } from '../../contexts/PreferencesContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  AlertCircle,
  Calendar,
  Eye,
  FileText,
  Folder,
  FolderTree,
  HardDrive,
  Info,
  Loader2,
  Upload,
  User,
} from 'lucide-react';
import { DetailField, DetailLabel } from './DocumentDetailFields';
import DocumentHistoryTab from './DocumentHistoryTab';

interface FolderDetailsDialogProps {
  /** The folder to describe — main folder or sub-folder, at any depth. */
  category: CategoryWithDocuments;
  onClose: () => void;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** "Private — …" / "Public — …" / "Shared with A, B and 2 other companies". */
function describeAccess(access: FolderDetails['access']): string {
  if (access.level === 'public') return 'Public — every supplier on the platform';
  if (access.level === 'private') return 'Private — only your organisation';
  const names = [...access.shared_with];
  if (access.hidden_count > 0) {
    names.push(plural(access.hidden_count, 'other company', 'other companies'));
  }
  return `Shared with ${names.join(', ')}`;
}

/**
 * CR-7754: a folder's Details window, opened from the folder's ⋮ menu or the
 * Details button on an open folder. The Details tab shows where the folder
 * sits, who made it, what it holds and who can see it; the History tab lists
 * who did what to the folder and its contents, newest first. Counts and
 * history only cover what the viewer can see. Customer side only.
 *
 * @param category - the folder to describe
 * @param onClose - called when the window is dismissed
 */
export default function FolderDetailsDialog({ category, onClose }: FolderDetailsDialogProps) {
  const { formatDateTime } = useFormatters();
  const [tab, setTab] = useState<'details' | 'history'>('details');
  const [details, setDetails] = useState<FolderDetails | null>(null);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [history, setHistory] = useState<DocumentHistoryEntry[]>([]);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!category.id) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      documentsAPI.getCategoryDetails(category.id)
        .then((res) => { if (!cancelled) setDetails(res.data); })
        .catch((err: unknown) => {
          if (!cancelled) setDetailsError(getApiErrorMessage(err, 'Could not load folder details.'));
        }),
      documentsAPI.getCategoryHistory(category.id)
        .then((res) => { if (!cancelled) setHistory(res.data); })
        .catch((err: unknown) => {
          if (!cancelled) setHistoryError(getApiErrorMessage(err, 'Could not load folder history.'));
        }),
    ]).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [category.id]);

  const location = details ? details.path.join(' › ') : category.name;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] p-0 gap-0 flex flex-col overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <div className="flex items-start gap-3 min-w-0">
            <div className="p-2 rounded-lg bg-muted text-muted-foreground shrink-0">
              <Folder className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <DialogTitle className="text-lg leading-tight break-words">{category.name}</DialogTitle>
              <DialogDescription className="mt-1 break-words">{location}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <Tabs
          value={tab}
          onValueChange={(v) => setTab(v === 'history' ? 'history' : 'details')}
          className="flex-1 flex flex-col overflow-hidden"
        >
          <div className="px-6 pt-4">
            <TabsList>
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="history">History</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="details" className="flex-1 overflow-y-auto px-6 py-4 mt-0">
            {loading && !details ? (
              <div className="flex items-center justify-center py-12 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                Loading folder details...
              </div>
            ) : detailsError || !details ? (
              <div className="flex items-center justify-center gap-2 py-8 text-muted-foreground">
                <AlertCircle className="h-4 w-4" />
                {detailsError || 'Could not load folder details.'}
              </div>
            ) : (
              <div className="space-y-4">
                {details.description && (
                  <div>
                    <DetailLabel icon={Info} text="Description" />
                    <p className="text-sm whitespace-pre-wrap break-words">{details.description}</p>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <DetailField icon={FolderTree} label="Location" value={location} />
                  <DetailField icon={Eye} label="Access" value={describeAccess(details.access)} />
                  <DetailField icon={Calendar} label="Created" value={formatDateTime(details.created_at)} />
                  <DetailField icon={User} label="Created by" value={details.created_by || 'Not recorded'} />
                  <DetailField icon={FileText} label="Files in this folder" value={String(details.direct_file_count)} />
                  <DetailField icon={Folder} label="Sub-folders" value={String(details.subfolder_count)} />
                  <DetailField
                    icon={FileText}
                    label="Files including sub-folders"
                    value={String(details.total_file_count)}
                  />
                  <DetailField icon={HardDrive} label="Total size" value={details.total_size_display} />
                  <DetailField
                    icon={Upload}
                    label="Last upload"
                    value={details.last_upload
                      ? `${details.last_upload.title} — ${formatDateTime(details.last_upload.at)} by ${details.last_upload.by}`
                      : 'No files yet'}
                  />
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="history" className="flex-1 overflow-y-auto px-6 py-4 mt-0">
            <DocumentHistoryTab
              history={history}
              loading={loading}
              error={historyError}
              emptyMessage="No activity recorded for this folder yet."
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
